import { ControlApiError } from "./api";

export const MAX_POLL_BACKOFF_MS = 5 * 60_000;

export function nextPollDelay(
  intervalMs: number,
  consecutiveFailures: number,
  maximumMs = MAX_POLL_BACKOFF_MS
): number {
  const exponent = Math.max(0, Math.min(consecutiveFailures, 10));
  return Math.min(intervalMs * 2 ** exponent, maximumMs);
}

export function isTerminalPollingError(error: unknown): boolean {
  return (
    error instanceof ControlApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

export function isPollingAvailable(): boolean {
  const visible = typeof document === "undefined" || document.visibilityState !== "hidden";
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  return visible && online;
}

export interface SequentialPollingOptions<T> {
  intervalMs: number;
  load: (signal: AbortSignal) => Promise<T>;
  onSuccess: (value: T) => void;
  onError: (error: unknown) => void;
  enabled?: boolean;
}

export function startSequentialPolling<T>(options: SequentialPollingOptions<T>): () => void {
  let stopped = false;
  let controller: AbortController | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight = false;
  let started = false;
  let consecutiveFailures = 0;
  let terminalFailure = false;

  function clearTimer() {
    clearTimeout(timer);
    timer = undefined;
  }

  function schedule() {
    clearTimer();
    if (
      stopped ||
      inFlight ||
      terminalFailure ||
      options.enabled === false ||
      !isPollingAvailable()
    ) {
      return;
    }
    timer = setTimeout(
      () => {
        timer = undefined;
        void poll();
      },
      nextPollDelay(options.intervalMs, consecutiveFailures)
    );
  }

  function handleAvailabilityChange() {
    if (!isPollingAvailable()) {
      clearTimer();
      return;
    }
    if (!started) void poll();
    else schedule();
  }

  async function poll() {
    if (stopped || inFlight || !isPollingAvailable()) return;
    started = true;
    inFlight = true;
    controller = new AbortController();
    try {
      const value = await options.load(controller.signal);
      consecutiveFailures = 0;
      if (!stopped) options.onSuccess(value);
    } catch (error) {
      consecutiveFailures += 1;
      terminalFailure = isTerminalPollingError(error);
      if (!stopped) options.onError(error);
    } finally {
      inFlight = false;
      schedule();
    }
  }

  document.addEventListener("visibilitychange", handleAvailabilityChange);
  window.addEventListener("online", handleAvailabilityChange);
  window.addEventListener("offline", handleAvailabilityChange);
  handleAvailabilityChange();

  return () => {
    stopped = true;
    clearTimer();
    controller?.abort();
    document.removeEventListener("visibilitychange", handleAvailabilityChange);
    window.removeEventListener("online", handleAvailabilityChange);
    window.removeEventListener("offline", handleAvailabilityChange);
  };
}
